import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Section } from '../../../../components/ui/Section';
import { CatalogueGovernanceClient, PriceChangeRow } from '../../../../app/services/api/catalogueGovernance';
import ApprovalStatusChip from './ApprovalStatusChip';
import RowActionsMenu from './RowActionsMenu';

const formatCurrency = (value: number) => `£${value.toFixed(2)}`;
const changePercent = (current: number, next: number) => (((next - current) / current) * 100).toFixed(1);

const PriceChangesTab = () => {
  const { t } = useTranslation('catalogueGovernance');
  const [rows, setRows] = useState<PriceChangeRow[]>([]);
  const [impactRow, setImpactRow] = useState<PriceChangeRow | null>(null);

  const load = () => CatalogueGovernanceClient.getPriceChanges().then(setRows);

  useEffect(() => {
    load();
  }, []);

  const handleAction = async (action: (id: string) => Promise<void>, id: string) => {
    await action(id);
    load();
  };

  return (
    <Section>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('priceChanges.columnSupplier', 'Supplier')}</TableCell>
            <TableCell>{t('priceChanges.columnCatalogue', 'Catalogue')}</TableCell>
            <TableCell>{t('priceChanges.columnProduct', 'Product')}</TableCell>
            <TableCell align="right">{t('priceChanges.columnCurrentPrice', 'Current price')}</TableCell>
            <TableCell align="right">{t('priceChanges.columnNewPrice', 'New price')}</TableCell>
            <TableCell align="right">{t('priceChanges.columnChangePercent', 'Change %')}</TableCell>
            <TableCell>{t('priceChanges.columnEffectiveDate', 'Effective date')}</TableCell>
            <TableCell>{t('priceChanges.columnApprovalStatus', 'Approval status')}</TableCell>
            <TableCell align="right">{t('priceChanges.columnAffectedRecipes', 'Affected recipes')}</TableCell>
            <TableCell align="right">{t('priceChanges.columnAffectedMenus', 'Affected menus')}</TableCell>
            <TableCell align="right">{t('priceChanges.columnAffectedSites', 'Affected sites')}</TableCell>
            <TableCell align="right">{t('priceChanges.columnEstCostImpact', 'Est. cost impact')}</TableCell>
            <TableCell>{t('priceChanges.columnActions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={13}>{t('priceChanges.emptyState', 'No price changes to show.')}</TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.supplier}</TableCell>
              <TableCell>{row.catalogue}</TableCell>
              <TableCell>{row.product}</TableCell>
              <TableCell align="right">{formatCurrency(row.currentPrice)}</TableCell>
              <TableCell align="right">{formatCurrency(row.newPrice)}</TableCell>
              <TableCell align="right">
                <Typography color={row.newPrice > row.currentPrice ? 'error.main' : 'success.main'}
                  variant="textSM"
                  fontWeight={600}>
                  {row.newPrice > row.currentPrice ? '+' : ''}{changePercent(row.currentPrice, row.newPrice)}%
                </Typography>
              </TableCell>
              <TableCell>{row.effectiveDate}</TableCell>
              <TableCell><ApprovalStatusChip status={row.approvalStatus} /></TableCell>
              <TableCell align="right">{row.affectedRecipes}</TableCell>
              <TableCell align="right">{row.affectedMenus}</TableCell>
              <TableCell align="right">{row.affectedSites}</TableCell>
              <TableCell align="right">{formatCurrency(row.estimatedCostImpact)}</TableCell>
              <TableCell>
                <RowActionsMenu actions={[
                  {
                    label: t('priceChanges.actionApprove', 'Approve'),
                    disabled: row.approvalStatus === 'Approved',
                    onClick: () => handleAction(CatalogueGovernanceClient.approvePriceChange.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('priceChanges.actionReject', 'Reject'),
                    disabled: row.approvalStatus === 'Rejected',
                    destructive: true,
                    onClick: () => handleAction(CatalogueGovernanceClient.rejectPriceChange.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('priceChanges.actionRequestInfo', 'Request info'),
                    onClick: () => handleAction(CatalogueGovernanceClient.requestPriceChangeInfo.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('priceChanges.actionViewImpact', 'View impact'),
                    onClick: () => setImpactRow(row),
                  },
                ]} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!impactRow}
        onClose={() => setImpactRow(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>{t('priceChanges.impactDialogTitle', 'Impact of price change - {{product}}', { product: impactRow?.product })}</DialogTitle>
        <DialogContent>
          <Stack spacing={1}
            sx={{ pb: 2 }}>
            <Typography variant="textSM">{t('priceChanges.impactAffectedRecipes', 'Affected recipes: {{count}}', { count: impactRow?.affectedRecipes })}</Typography>
            <Typography variant="textSM">{t('priceChanges.impactAffectedMenus', 'Affected menus: {{count}}', { count: impactRow?.affectedMenus })}</Typography>
            <Typography variant="textSM">{t('priceChanges.impactAffectedSites', 'Affected sites: {{count}}', { count: impactRow?.affectedSites })}</Typography>
            <Typography variant="textSM">
              {t('priceChanges.impactEstimatedCost', 'Estimated cost impact: {{value}}', { value: impactRow ? formatCurrency(impactRow.estimatedCostImpact) : '-' })}
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </Section>
  );
};

export default PriceChangesTab;
